import React from 'react';
import { EventRound, EventTeam, HitsterEvent } from '../../types';
import { getTeamStartYearForRound, getTimelineLayout } from '../../utils/roundTimeline';

export type RowChecks = {
  positionCorrect: boolean;
  yearCorrect: boolean;
  titleCorrect: boolean;
  artistCorrect: boolean;
};

export type TeamChecks = Record<string, RowChecks>;

export const createEmptyChecks = (): RowChecks => ({
  positionCorrect: false,
  yearCorrect: false,
  titleCorrect: false,
  artistCorrect: false,
});

type ScoreField = keyof RowChecks;

type ScoreSheetPagesProps = {
  event: HitsterEvent;
  rounds: EventRound[];
  teams: EventTeam[];
  teamChecks?: Record<string, TeamChecks>;
  onToggleCheck?: (teamId: string, rowKey: string, field: ScoreField) => void;
  scanCounts?: Record<string, number>;
  onScanIconClick?: (songId: string, songTitle: string) => void;
};

const ScoreSheetPages: React.FC<ScoreSheetPagesProps> = ({
  event,
  rounds,
  teams,
  teamChecks = {},
  onToggleCheck,
  scanCounts,
  onScanIconClick,
}) => {
  const sortedRounds = [...rounds].sort((left, right) => left.order - right.order);

  return (
    <div className="score-sheet-pages">
      {teams.flatMap(team => {
        const checksForTeam = teamChecks[team.id] || {};

        return sortedRounds.map(round => {
          const roundLabel = `Ronde ${round.order}`;
          const roundSubtitle = round.name && round.name !== roundLabel ? round.name : '';
          const maxPoints = Math.max(round.songs.length, 10) * 4;
          const teamStartYear = getTeamStartYearForRound(event, round, team.id);
          const timeline = getTimelineLayout(round.songs, teamStartYear);
          const timelineRows = [
            ...timeline.leftCards.map(card => ({
              rowKey: `${round.id}:${card.song.id}`,
              song: card.song,
              displayOrder: card.cardNumber,
              year: card.song.year,
              title: card.song.title,
              artist: card.song.artist,
              isStartRow: false,
            })),
            {
              rowKey: `${round.id}:start-row:${team.id}`,
              song: undefined,
              displayOrder: 'START',
              year: teamStartYear,
              title: 'Startnummer',
              artist: '',
              isStartRow: true,
            },
            ...timeline.rightCards.map(card => ({
              rowKey: `${round.id}:${card.song.id}`,
              song: card.song,
              displayOrder: card.cardNumber,
              year: card.song.year,
              title: card.song.title,
              artist: card.song.artist,
              isStartRow: false,
            })),
          ];

          const rowCount = Math.max(timelineRows.length, 11);
          const rows = Array.from({ length: rowCount }, (_, index) => {
            const row = timelineRows[index];

            return row || {
              rowKey: `${round.id}:empty-row:${team.id}:${index + 1}`,
              song: undefined,
              displayOrder: '',
              year: '',
              title: '',
              artist: '',
              isStartRow: false,
            };
          });

          const teamTotal = rows.reduce((sum, row) => {
            const checks = checksForTeam[row.rowKey] || createEmptyChecks();
            return sum
              + Number(checks.positionCorrect)
              + Number(checks.yearCorrect)
              + Number(checks.titleCorrect)
              + Number(checks.artistCorrect);
          }, 0);

          return (
            <section key={`${team.id}-${round.id}`} className="score-sheet-page">
              <div className="score-sheet-page-inner">
                <div className="score-sheet-header">
                  <div className="sheet-title-block">
                    <div className="event-name">{event.name}</div>
                    <div className="round-name">{roundLabel}</div>
                    {roundSubtitle && <div className="round-subtitle">{roundSubtitle}</div>}
                  </div>
                </div>

                <div className="score-sheet-meta-grid">
                  <div className="info-panel team-info-panel">
                    <span className="info-label">Team</span>
                    <span className="info-value team-name-value">{team.name}</span>
                  </div>
                  <div className="info-panel round-info-panel">
                    <span className="info-label">Ronde</span>
                    <span className="info-value">{roundLabel}</span>
                  </div>
                  <div className="info-panel start-year-panel">
                    <span className="info-label">Startjaar</span>
                    <span className="info-value start-year-value">{teamStartYear}</span>
                  </div>
                </div>

                <table className="score-table">
                  <thead>
                    <tr className="thead-groups">
                      <th className="col-po" rowSpan={2}>PO</th>
                      <th className="col-year" rowSpan={2}>Jaar</th>
                      <th className="col-score-pj" colSpan={2}>Score positie + jaar</th>
                      <th className="col-title" rowSpan={2}>Titel</th>
                      <th className="col-score-title" rowSpan={2}>Score titel</th>
                      <th className="col-artist" rowSpan={2}>Artiest</th>
                      <th className="col-score-artist" rowSpan={2}>Score artiest</th>
                      <th className="col-total" rowSpan={2}>Tot</th>
                      {onScanIconClick && <th className="col-scan" rowSpan={2}>📷</th>}
                    </tr>
                    <tr className="thead-subheaders">
                      <th className="col-check">Pos</th>
                      <th className="col-check">Jaar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const checks = checksForTeam[row.rowKey] || createEmptyChecks();
                      const rowTotal = Number(checks.positionCorrect)
                        + Number(checks.yearCorrect)
                        + Number(checks.titleCorrect)
                        + Number(checks.artistCorrect);
                      const rowClassName = row.isStartRow
                        ? 'start-row'
                        : index % 2 === 0
                          ? 'row-even'
                          : 'row-odd';
                      const canToggle = Boolean(onToggleCheck) && !row.isStartRow && Boolean(row.song);

                      return (
                        <tr key={row.rowKey} className={rowClassName}>
                          <td className="col-po">{row.displayOrder}</td>
                          <td className="col-year">{row.year}</td>
                          <td className="col-check">
                            <label className={`score-checkbox-label ${!canToggle ? 'disabled' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checks.positionCorrect}
                                disabled={!canToggle}
                                onChange={() => onToggleCheck?.(team.id, row.rowKey, 'positionCorrect')}
                                aria-label={`Positie correct voor ${team.name} in ${roundLabel}, rij ${index + 1}`}
                              />
                              <span className="checkbox-print" />
                            </label>
                          </td>
                          <td className="col-check">
                            <label className={`score-checkbox-label ${!canToggle ? 'disabled' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checks.yearCorrect}
                                disabled={!canToggle}
                                onChange={() => onToggleCheck?.(team.id, row.rowKey, 'yearCorrect')}
                                aria-label={`Jaar correct voor ${team.name} in ${roundLabel}, rij ${index + 1}`}
                              />
                              <span className="checkbox-print" />
                            </label>
                          </td>
                          <td className="col-title">{row.title}</td>
                          <td className="col-check">
                            <label className={`score-checkbox-label ${!canToggle ? 'disabled' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checks.titleCorrect}
                                disabled={!canToggle}
                                onChange={() => onToggleCheck?.(team.id, row.rowKey, 'titleCorrect')}
                                aria-label={`Titel correct voor ${team.name} in ${roundLabel}, rij ${index + 1}`}
                              />
                              <span className="checkbox-print" />
                            </label>
                          </td>
                          <td className="col-artist">{row.artist}</td>
                          <td className="col-check">
                            <label className={`score-checkbox-label ${!canToggle ? 'disabled' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checks.artistCorrect}
                                disabled={!canToggle}
                                onChange={() => onToggleCheck?.(team.id, row.rowKey, 'artistCorrect')}
                                aria-label={`Artiest correct voor ${team.name} in ${roundLabel}, rij ${index + 1}`}
                              />
                              <span className="checkbox-print" />
                            </label>
                          </td>
                          <td className="col-total total-value">{row.song ? rowTotal : ''}</td>
                          {onScanIconClick && (
                            <td className="col-scan">
                              {row.song && (scanCounts?.[row.song.id] || 0) > 0 ? (
                                <button
                                  className="scan-sub-icon-btn"
                                  onClick={() => onScanIconClick(row.song!.id, row.title || '')}
                                  title="Scan inzendingen bekijken"
                                >
                                  📷 {scanCounts![row.song.id]}
                                </button>
                              ) : null}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td className="total-label" colSpan={8}>Totaal</td>
                      <td className="col-total grand-total">{teamTotal}</td>
                      {onScanIconClick && <td className="col-scan"></td>}
                    </tr>
                  </tfoot>
                </table>

                <div className="score-sheet-footer">
                  <span>Per rij maximaal 4 punten</span>
                  <span>Bladtotaal maximaal {maxPoints} punten</span>
                </div>
              </div>
            </section>
          );
        });
      })}
    </div>
  );
};

export default ScoreSheetPages;
